/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useState } from 'react'
import { Card, Checkbox } from '@sede-enterprise/shell-ds-react-framework';
import SensorCRDetails from './SensorCRDetails';
import DetailsTopContributors from '../../components/DetailsTopContributors/DetailsTopContributors';
import { calculateFixedYRange, generateRequests, getSiteType } from '../../util/helperFunctions';
import { generateCheckboxConfig } from '../../util/checkboxConfig';
import { INTERVAL_OPTIONS } from '../../util/constants';
import useUpdateChart from '../../util/hooks/useUpdateChart';
import usePredictedWallThickness from './hooks/usePredictedWallThickness';
import useMissingData from './hooks/useMissingData';
import useGraphData from './hooks/useGraphData';
import useFetchFaultySensorData from './hooks/useFetchFaultySensorData';
import useFetchMissingFeature from './hooks/useFetchMissingFeature';
import useForecastWallThickness from './hooks/useForecastWallThickness';
import MetricChart from '../../components/MetricChart/MetricChart';

const SensorDetailsChart = (props) => {

    const {
        units,
        prodUnit,
        sensorID,
        site,
        metricData,
        sensorData,
        loading,
        start,
        setStart,
        end,
        setEnd,
        interval,
        setSelectedInterval,
        forecastMonths,
        setSelectedMonths,
        forecastMonthsOpts,
        setRequestParams,
        formDirty,
        refreshChartData,
    } = props;

    const { isNorco, isNorcoWest, isRhienland, isSyngas, isBonga, isScotfordHCU, isScotfordADU, isBukom, isNorcoEast, isPernis } = getSiteType(site, prodUnit);

    const PredictedWallThicknessMetric = isRhienland
        ? "LiveModelPredictedWallThickness_RHE"
        : "LiveModelPredictedWallThickness";

    const [isMeasuredWallThicknessChecked, setIsMeasuredWallThicknessChecked] = useState(true);
    const [sensorCheckboxes, setSensorCheckboxes] = useState(generateCheckboxConfig({ units, site, prodUnit, isNorco, isNorcoWest, isRhienland, isSyngas, isBonga, isScotfordHCU }));
    const [annotationArray, setannotationArray] = useState([]);
    const [missingTrue, setMissingTrue] = useState(false);
    const [fetchPredWT, setFetchPredWT] = useState(true);

    // const { predWTData, predLoading } = usePredictedWallThickness(
    //     useMemo(
    //         () => ({
    //             fetchPredWT,
    //             sensorID,
    //             units,
    //             PredictedWallThicknessMetric,
    //             start,
    //             end,
    //             interval,
    //         }),
    //         [fetchPredWT, sensorID, units, PredictedWallThicknessMetric, start, end, interval]
    //     )
    // );
    // console.log(predWTData)

    const { missingData } = useMissingData({ missingTrue, sensorID });
    
    useEffect(() => {
        const updatedCheckboxConfig = generateCheckboxConfig({
            units,
            site,
            prodUnit,
            isNorco,
            isNorcoWest,
            isRhienland,
            isSyngas,
            isBonga,
            isScotfordHCU,
            isBukom,
            isNorcoEast
        });

        setSensorCheckboxes((prevCheckboxes) => {
            const mergedCheckboxes = Object.keys(updatedCheckboxConfig).reduce((acc, key) => {
                acc[key] = {
                    ...updatedCheckboxConfig[key],
                    enabled: prevCheckboxes[key]?.enabled ?? updatedCheckboxConfig[key].enabled, // Preserve 'enabled' if it exists
                };
                return acc;
            }, {});

            return {
                ...mergedCheckboxes,
                InterpolatedMeasuredWT: {
                    ...mergedCheckboxes.InterpolatedMeasuredWT,
                    available: !sensorData?.isRealSensor && (isNorcoWest || isScotfordADU),
                },
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        units,
        site,
        prodUnit,
        isNorco,
        isNorcoWest,
        isRhienland,
        isSyngas,
        isBonga,
        isScotfordHCU,
        sensorData,
    ]);

    const sensorRequests = useMemo(() => {
        return generateRequests(sensorCheckboxes, sensorID, "SensorExpression", "sensor");
    }, [sensorCheckboxes, sensorID]);

    useEffect(() => {
        if (!!sensorRequests.length) {
            console.log("about to call refreshChartData with requests:");
            console.log(sensorRequests)
            refreshChartData([...sensorRequests]);
        } else {
            refreshChartData([]);
        }
    }, [sensorRequests, refreshChartData]);

    const { forecastWallThicknessLoading, forecast_wallthickness, isForecastWallThickness, lifeExpectancy } = useForecastWallThickness({
        sensorID,
        start,
        end,
        sensorCheckboxes,
        interval,
        forecastMonths,
        isNorco,
        units,
        PredictedWallThicknessMetric,
    })


    const { resetWTIndexes, missingFeature } = useFetchMissingFeature({
        sensorID, start, end, interval
    })

    const { faultySensorData } = useFetchFaultySensorData({ start, end, interval, sensorID, isMeasuredWallThicknessChecked, sensorData })

    const { graphData } = useGraphData({
        metricData,
        forecast_wallthickness,
        resetWTIndexes,
        isMeasuredWallThicknessChecked,
        // predWTData,
        faultySensorData,
        missingFeature,
        units,
        loading,
        sensorData
    });


    const sensorCheckboxClicked = (id) => {

        if (id === "MeasuredWallThickness") {
            setIsMeasuredWallThicknessChecked(!isMeasuredWallThicknessChecked);
        }

        if (id === "MissingFeatures") {
            setMissingTrue(!missingTrue);
        }

        if (id === "PredictedWallThickness") {
            setFetchPredWT(!fetchPredWT)
        }

        if (id === "PredictedCorrosionRateConfidenceBandLowerValue") {
            setSensorCheckboxes({
                ...sensorCheckboxes,
                PredictedCorrosionRateConfidenceBandUpperValue: {
                    ...sensorCheckboxes.PredictedCorrosionRateConfidenceBandUpperValue,
                    enabled:
                        !sensorCheckboxes.PredictedCorrosionRateConfidenceBandUpperValue
                            .enabled,
                },
                PredictedCorrosionRateConfidenceBandLowerValue: {
                    ...sensorCheckboxes.PredictedCorrosionRateConfidenceBandLowerValue,
                    enabled:
                        !sensorCheckboxes.PredictedCorrosionRateConfidenceBandLowerValue
                            .enabled,
                },
            });
        } else {

            setSensorCheckboxes({
                ...sensorCheckboxes,
                [id]: {
                    ...sensorCheckboxes[id],
                    enabled: !sensorCheckboxes[id].enabled,
                },
            });
        }
    };



    const updateChart = useUpdateChart({
        setRequestParams,
        start,
        end,
        interval,
    });

    const [clickedDate, setclickedDate] = useState(null);
    const [clickedID, setclickedID] = useState(null);

    const getTopContributorsClicked = (dateData) => {
        setclickedDate(dateData);
        setclickedID(sensorID);
    };
    const fixedYRange = () => {
        const fixedYRangeVariables = [
            'Measured wall thickness',
            'Predicted wall thickness',
            'Forecasted Wall Thickness',
            'Measured corrosion rate',
            'Predicted corrosion rate',
            'Predicted corrosion rate confidence band',
            'High measured corrosion rate',
            'High predicted corrosion rate',
            'Alert threshold',
        ];
        return calculateFixedYRange(graphData, units, fixedYRangeVariables);
    };

    return (
        <div className='features-container' id='sensor-details-chart-id-1'>

            <SensorCRDetails units={units}
                sensorID={sensorID}
                start={start}
                end={end} />
            <br />
            <Card className='details-chart-card' bodyPadding={false} id='sensor-details-chart-id-5'>
                <div className='graphs-container' id='sensor-details-chart-id-6'>
                    <MetricChart
                        yaxes={{
                            yaxis: { fixedrange: false, side: "left" },
                            yaxis2: {
                                fixedrange: false,
                                side: "right",
                                overlaying: "y",
                                anchor: "x",
                            },
                        }}
                        missing={missingTrue}
                        missingData={missingData}
                        data={graphData}
                        metricData={graphData}
                        loading={loading || forecastWallThicknessLoading}
                        start={start}
                        setStart={setStart}
                        end={end}
                        setEnd={setEnd}
                        sensorData={sensorData}
                        interval={interval}
                        annotationArray={annotationArray}
                        onClick={getTopContributorsClicked}
                        fixedYRange={fixedYRange()}
                        setSelectedInterval={setSelectedInterval}
                        forecastMonths={forecastMonths}
                        setSelectedMonths={setSelectedMonths}
                        forecastMonthsOpts={forecastMonthsOpts}
                        isForecastWallThickness={isForecastWallThickness}
                        lifeExpectancyInYears={lifeExpectancy}
                        intervalOpts={INTERVAL_OPTIONS}
                        formDirty={formDirty}
                        onSubmit={updateChart}
                    />
                </div>
                <div className='metric-chart-checkboxes' id='sensor-details-chart-id-7'>
                    <div className='metric-checkboxes-row' id='sensor-details-chart-id-8'>
                        <p className='data-label' id='sensor-details-chart-id-9'>Features</p>
                        <div className='checkboxes-container' id='sensor-details-chart-id-10'>
                            {Object.entries(sensorCheckboxes)
                                .filter(
                                    ([key, value]) =>
                                        value.group === 1 && value.available
                                )
                                .map((k) => k[0])
                                .map((key) => {
                                    return (
                                        <Checkbox
                                            id='sensor-details-chart-id-11'
                                            className='checkbox-item'
                                            key={key}
                                            disabled={loading}
                                            checked={sensorCheckboxes[key].enabled}
                                            label={sensorCheckboxes[key].name + " " + sensorCheckboxes[key].unit}
                                            onChange={() => sensorCheckboxClicked(key)} />
                                    );
                                })}
                        </div>
                    </div>
                    <div className='metric-checkboxes-row' id='sensor-details-chart-id-12'>
                        <p className='data-label' id='sensor-details-chart-id-13'>Features (Second Y-axis)</p>
                        <div className='checkboxes-container' id='sensor-details-chart-id-14'>
                            {Object.entries(sensorCheckboxes)
                                .filter(([key, value]) => value.group === 2 && value.available)
                                .map((k) => k[0])
                                .map((key) => {
                                    return (
                                        <Checkbox
                                            id='sensor-details-chart-id-15'
                                            className='checkbox-item'
                                            key={key}
                                            disabled={loading}
                                            checked={sensorCheckboxes[key].enabled}
                                            label={
                                                sensorCheckboxes[key].name +
                                                " " +
                                                sensorCheckboxes[key].unit
                                            }
                                            onChange={() => sensorCheckboxClicked(key)}
                                        />
                                    );
                                })}
                        </div>
                    </div>
                    <div className='metric-checkboxes-row' id='sensor-details-chart-id-16'>
                        <p className='data-label' id='sensor-details-chart-id-17'>Predictions</p>
                        <div className='checkboxes-container' id='sensor-details-chart-id-18'>
                            {Object.entries(sensorCheckboxes)
                                .filter(([key, value]) => value.group === 3 && value.available)
                                .map((k) => k[0])
                                .map((key) => {
                                    return (
                                        <Checkbox key={key}
                                            id='sensor-details-chart-id-19'
                                            className='checkbox-item'
                                            disabled={loading}
                                            checked={sensorCheckboxes[key].enabled}
                                            label={key === "PredictedCorrosionRateConfidenceBandLowerValue" ? "Predicted corrosion rate confidence band value " + sensorCheckboxes[key].unit : sensorCheckboxes[key].name + " " + sensorCheckboxes[key].unit}
                                            onChange={() => sensorCheckboxClicked(key)} />
                                    );
                                })}
                        </div>
                    </div>
                    <div className='metric-checkboxes-row' id='sensor-details-chart-id-20'>
                        <p className='data-label' id='sensor-details-chart-id-21'>Alerts</p>
                        <div className='checkboxes-container' id='sensor-details-chart-id-22'>
                            {Object.entries(sensorCheckboxes)
                                .filter(([key, value]) => value.group === 4)
                                .map((k) => k[0])
                                .map((key) => {
                                    return (
                                        <Checkbox key={key}
                                            id='sensor-details-chart-id-23'
                                            className='checkbox-item'
                                            disabled={loading}
                                            checked={sensorCheckboxes[key].enabled}
                                            label={sensorCheckboxes[key].name + " " + sensorCheckboxes[key].unit}
                                            onChange={() => sensorCheckboxClicked(key)} />
                                    );
                                })}
                        </div>
                    </div>
                </div>
            </Card>
            <br />
            <br />
            <DetailsTopContributors
                units={units}
                pathPrefix=""
                corrosionLocationID={sensorID}
                start={start}
                end={end}
                isRealSensor={sensorData?.isRealSensor && !isPernis}
            />
        </div>
    )
}

export default SensorDetailsChart